/**
 * Collection filters
 *
 * Submits the filter <form> whenever a value changes so the collection page
 * reloads with the updated `?filter.*` query parameters (which Shopify uses to
 * filter `collection.products`).
 *
 * On desktop the sidebar filters apply instantly on change (checkboxes/selects
 * submit immediately; number inputs are debounced). On mobile the filters live
 * in a slide-in drawer, so we don't reload on every tap — the shopper picks
 * several values and confirms with the "apply" button, which then submits once.
 * Without JavaScript the form always works via its submit button.
 */
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.applyButtons = this.querySelectorAll('[data-filters-apply]');
    for (const button of this.applyButtons) {
      button.addEventListener('click', this.handleApplyClick);
    }
    this.mobileQuery = window.matchMedia('(max-width: 999px)');
    this.mobileQuery.addEventListener('change', this.updateApplyVisibility);
    this.updateApplyVisibility();

    this.form.addEventListener('change', this.handleChange);

    this.setupDrawer();
    this.setupShowMore();
  }

  disconnectedCallback() {
    clearTimeout(this.debounceTimer);
    document.removeEventListener('keydown', this.handleKeydown);
    this.mobileQuery?.removeEventListener('change', this.updateApplyVisibility);
  }

  /** True while the mobile drawer layout is active. */
  isMobile() {
    return this.mobileQuery?.matches ?? false;
  }

  /** The apply button is only useful on mobile (manual submit) or without JS. */
  updateApplyVisibility = () => {
    const mobile = this.isMobile();
    for (const button of this.applyButtons ?? []) {
      button.hidden = !mobile;
    }
  };

  handleApplyClick = (event) => {
    event.preventDefault();
    this.submitForm();
  };

  /* ----- Mobile drawer ----- */
  setupDrawer() {
    this.root = this.closest('.collection-products');
    this.drawer = this.closest('[data-filters-drawer]');
    if (!this.root || !this.drawer) return;

    this.backdrop = this.root.querySelector('[data-filters-backdrop]');
    this.openButtons = this.root.querySelectorAll('[data-filters-open]');
    this.closeButtons = this.root.querySelectorAll('[data-filters-close]');

    for (const button of this.openButtons) button.addEventListener('click', this.openDrawer);
    for (const button of this.closeButtons) button.addEventListener('click', this.closeDrawer);
    this.backdrop?.addEventListener('click', this.closeDrawer);
  }

  openDrawer = () => {
    this.drawer?.classList.add('is-open');
    if (this.backdrop) this.backdrop.hidden = false;
    document.documentElement.classList.add('collection-filters-open');
    for (const button of this.openButtons ?? []) button.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.handleKeydown);
  };

  closeDrawer = () => {
    this.drawer?.classList.remove('is-open');
    if (this.backdrop) this.backdrop.hidden = true;
    document.documentElement.classList.remove('collection-filters-open');
    for (const button of this.openButtons ?? []) button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.handleKeydown);
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape') this.closeDrawer();
  };

  /* ----- "Show more" for long filter value lists ----- */
  setupShowMore() {
    for (const button of this.querySelectorAll('[data-filters-show-more]')) {
      button.addEventListener('click', this.toggleShowMore);
    }
  }

  toggleShowMore = (event) => {
    const button = event.currentTarget;
    const details = button.closest('.collection-filters__details');
    const list = details?.querySelector('.collection-filters__values');
    if (!list) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    const next = !expanded;

    for (const item of list.querySelectorAll('.collection-filters__value--extra')) {
      item.hidden = !next;
    }

    button.setAttribute('aria-expanded', String(next));

    const text = button.querySelector('.collection-filters__show-more-text');
    if (text) {
      text.textContent = next ? text.dataset.lessLabel : text.dataset.moreLabel;
    }
  };

  handleChange = (event) => {
    // On mobile the shopper applies filters explicitly via the drawer's button,
    // so a single tap doesn't reload the page and close the drawer.
    if (this.isMobile()) return;

    const target = event.target;
    const isInstant =
      target instanceof HTMLSelectElement ||
      (target instanceof HTMLInputElement && (target.type === 'checkbox' || target.type === 'radio'));

    clearTimeout(this.debounceTimer);

    if (isInstant) {
      this.submitForm();
    } else {
      this.debounceTimer = setTimeout(() => this.submitForm(), 700);
    }
  };

  submitForm() {
    // Don't litter the URL with empty `?filter.v.price.gte=` style params.
    for (const input of this.form.querySelectorAll('input[type="number"]')) {
      input.disabled = input.value.trim() === '';
    }
    this.form.submit();
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
